// About page: enterprise profile → company story → background strengths →
// org chart → department functions → chairman's message pull-quote → culture.
// Content from company-profile/content.md via the `about` namespace. Owned by Agent D.
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { DEFAULT_LOCALE } from "../i18n";
import { fadeUp, stagger } from "../motion/variants";
import SectionBlock from "../components/SectionBlock";
import OrgChart from "../components/OrgChart";
import PageMeta from "../components/PageMeta";

const FUNC_KEYS = ["admin", "shipping", "freight", "trade", "finance"] as const;

export default function About() {
  const { t } = useTranslation("about");
  const { lang } = useParams();
  const current = lang ?? DEFAULT_LOCALE;
  const isZh = current === "zh"; // zh-first: Chinese heading leads, Latin caption follows

  const storyParas = t("story.paragraphs", { returnObjects: true }) as string[];
  const bgParas = t("background.paragraphs", { returnObjects: true }) as string[];
  const bgHighlights = t("background.highlights", { returnObjects: true }) as {
    title: string;
    desc: string;
  }[];
  const chairmanParas = t("chairman.paragraphs", { returnObjects: true }) as string[];
  const cultureValues = t("culture.values", { returnObjects: true }) as {
    title: string;
    desc: string;
  }[];

  return (
    <div className="page page-about">
      <PageMeta title={t("meta.title")} description={t("meta.description")} lang={current} />

      {/* Page hero with skyline band */}
      <section className="relative isolate overflow-hidden bg-[var(--brand-tint-3)]">
        <div className="absolute inset-0 -z-10">
          <img
            src="/images/skyline-wide.jpg"
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover object-bottom opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 to-[var(--brand-tint-3)]" />
        </div>
        <div className="mx-auto w-full max-w-6xl px-6 py-24">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
            className="max-w-3xl"
          >
            {isZh ? (
              <>
                <motion.h1
                  variants={fadeUp}
                  className="font-zh text-[var(--text-4xl)] font-bold leading-[var(--leading-heading)] text-[var(--ink)]"
                >
                  {t("hero.title")}
                </motion.h1>
                <motion.p
                  variants={fadeUp}
                  className="mt-3 font-sans text-[var(--text-sm)] font-medium uppercase tracking-[var(--tracking-eyebrow)] text-[var(--brand-azure-text)]"
                >
                  {t("hero.eyebrowEn")}
                </motion.p>
              </>
            ) : (
              <>
                <motion.p
                  variants={fadeUp}
                  className="font-sans text-[var(--text-sm)] font-medium uppercase tracking-[var(--tracking-eyebrow)] text-[var(--brand-azure-text)]"
                >
                  {t("hero.eyebrowEn")}
                </motion.p>
                <motion.h1
                  variants={fadeUp}
                  className="mt-4 font-zh text-[var(--text-4xl)] font-bold leading-[var(--leading-heading)] text-[var(--ink)]"
                >
                  {t("hero.title")}
                </motion.h1>
              </>
            )}
            <motion.p
              variants={fadeUp}
              className="mt-5 text-[var(--text-lg)] leading-[var(--leading-body)] text-[var(--brand-slate-text)]"
            >
              {t("hero.subtitle")}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Company story */}
      <SectionBlock
        eyebrowEn={t("story.eyebrowEn")}
        heading={t("story.title")}
      >
        <div className="grid gap-6">
          {storyParas.map((p, i) => (
            <motion.p
              key={i}
              variants={fadeUp}
              className="max-w-3xl text-[var(--text-lg)] leading-[var(--leading-body)] text-[var(--ink)]/85"
            >
              {p}
            </motion.p>
          ))}
        </div>
      </SectionBlock>

      {/* Background strengths */}
      <SectionBlock
        eyebrowEn={t("background.eyebrowEn")}
        heading={t("background.title")}
        lead={bgParas[0]}
        surface="tint"
      >
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {bgHighlights.map((h) => (
            <motion.div
              key={h.title}
              variants={fadeUp}
              className="rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white p-6"
            >
              <h3 className="font-zh text-[var(--text-lg)] font-bold text-[var(--brand-azure)]">
                {h.title}
              </h3>
              <p className="mt-3 text-[var(--text-base)] leading-[var(--leading-body)] text-[var(--brand-slate-text)]">
                {h.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </SectionBlock>

      {/* Org chart */}
      <SectionBlock
        eyebrowEn={t("org.eyebrowEn")}
        heading={t("org.title")}
        lead={t("org.lead")}
        centered
      >
        <OrgChart />
      </SectionBlock>

      {/* Department functions */}
      <SectionBlock
        eyebrowEn={t("functions.eyebrowEn")}
        heading={t("functions.title")}
        surface="tint"
      >
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {FUNC_KEYS.map((key) => {
            const item = t(`functions.items.${key}`, { returnObjects: true }) as {
              name: string;
              points: string[];
            };
            return (
              <motion.div
                key={key}
                variants={fadeUp}
                className="flex flex-col rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white p-6"
              >
                <h3 className="font-zh text-[var(--text-lg)] font-bold text-[var(--ink)]">
                  {item.name}
                </h3>
                <ul className="mt-4 space-y-2.5">
                  {item.points.map((pt) => (
                    <li
                      key={pt}
                      className="flex gap-2.5 text-[var(--text-sm)] text-[var(--ink)]/80"
                    >
                      <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-[var(--brand-primary)]" />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </SectionBlock>

      {/* Chairman's message */}
      <SectionBlock
        eyebrowEn={t("chairman.eyebrowEn")}
        heading={t("chairman.title")}
      >
        <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr]">
          <motion.blockquote
            variants={fadeUp}
            className="rounded-[var(--radius-lg)] bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-azure)] p-8 font-zh text-[var(--text-2xl)] font-bold leading-snug text-white"
          >
            {t("chairman.quote")}
          </motion.blockquote>
          <div className="grid gap-5">
            {chairmanParas.map((p, i) => (
              <motion.p
                key={i}
                variants={fadeUp}
                className="text-[var(--text-base)] leading-[var(--leading-body)] text-[var(--ink)]/85"
              >
                {p}
              </motion.p>
            ))}
            <motion.p
              variants={fadeUp}
              className="mt-2 text-[var(--text-sm)] text-[var(--brand-slate-text)]"
            >
              — {t("chairman.signature")} · {t("chairman.date")}
            </motion.p>
          </div>
        </div>
      </SectionBlock>

      {/* Culture */}
      <SectionBlock
        eyebrowEn={t("culture.eyebrowEn")}
        heading={t("culture.title")}
        lead={t("culture.lead")}
        surface="tint"
        centered
      >
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {cultureValues.map((v) => (
            <motion.div
              key={v.title}
              variants={fadeUp}
              className="rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white p-6 text-center"
            >
              <h3 className="font-zh text-[var(--text-lg)] font-bold text-[var(--brand-azure)]">
                {v.title}
              </h3>
              <p className="mt-3 text-[var(--text-sm)] leading-[var(--leading-body)] text-[var(--brand-slate-text)]">
                {v.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </SectionBlock>
    </div>
  );
}
